/**
 * Another fan's profile screen: the three reads, run through
 * `buildFanProfileView`, then mounted in the frame's top-to-bottom order.
 * Kept as a pane, not folded into the route file, so a future members
 * screen can mount it without a navigation.
 */
import { useAwardGetForAccount } from "@sportsmet/contest-sdk/private";
import { usePoolSharedWithAccount } from "@sportsmet/pool-sdk/private";
import { useProfileGetForAccount } from "@sportsmet/profile-sdk/private";
import { Button, Skeleton } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { useAuth } from "../../auth/AuthProvider";
import { MedalsBlock } from "../awards/MedalsBlock";
import { isFollowState } from "../followers/followState";
import { FanFollowAction } from "./FanFollowAction";
import { FanFavoriteTeamsRow } from "./FanFavoriteTeamsRow";
import { FanMessageAction } from "./FanMessageAction";
import {
  AWARDS_UNAVAILABLE,
  MEDALS_SECTION_TITLE,
  MORE_ACTIONS_LABEL,
  PROFILE_MISSING_ID_LINE,
  PROFILE_PRIVATE_LINE,
  PROFILE_UNAVAILABLE_LINE,
  SCOPE_ALL_TIME_LABEL,
  SCOPE_TABS_LABEL,
  SCOPE_THIS_SEASON_LABEL,
} from "./fanProfileCopy";
import { FanProfileHeader } from "./FanProfileHeader";
import { buildFanProfileView } from "./fanProfileState";
import { SharedPoolsList, type typeSharedPoolRow } from "./SharedPoolsList";

export type typeFanProfilePaneProps = {
  accountId: string | undefined;
  /**
   * Takes no name. The actions sheet reads the account's name from the
   * profile query itself, because its open state lives in the URL and a
   * reload reopens it with no handoff from this button.
   */
  onOpenActions: () => void;
};

export const FanProfilePane = (props: typeFanProfilePaneProps): ReactNode => {
  const hasId = props.accountId !== undefined && props.accountId !== "";
  const { state: authState } = useAuth();
  const viewerUserId =
    authState.status === "signedIn" ? authState.user.userId : "";
  const isSelf = viewerUserId !== "" && viewerUserId === props.accountId;
  const profileQuery = useProfileGetForAccount(
    { accountId: props.accountId ?? "" },
    { enabled: hasId },
  );
  const isProfileReady = profileQuery.data?.data?.profile != null;
  // ISSUE-800. This read deliberately keeps the app's normal freshness.
  // A viewer who gives or takes back an award, then opens this screen, is
  // one session with one query cache. So invalidation reaches it.
  // `awardCustomGive` and `awardCustomTakeBack` both declare
  // `post /award/get-for-account` in their `reactQueryDependencies`.
  // Contrast `accountHome/AccountHomePane.tsx`, where the giver and the
  // recipient are different accounts, so it overrides its own freshness.
  const awardsQuery = useAwardGetForAccount(
    { accountId: props.accountId ?? "" },
    { enabled: hasId && isProfileReady },
  );
  const poolsQuery = usePoolSharedWithAccount(
    { accountId: props.accountId ?? "" },
    { enabled: hasId && isProfileReady && !isSelf },
  );

  const pools: typeSharedPoolRow[] = (poolsQuery.data?.data?.pools ?? []).map(
    (p) => ({
      poolId: p.poolId,
      ...(p.bannerImageUrl ? { bannerImageUrl: p.bannerImageUrl } : {}),
      ...(p.creatorHandle ? { creatorHandle: p.creatorHandle } : {}),
      contestType: p.contestType,
      sportKey: p.sportKey,
      name: p.name,
      leagueLabel: p.leagueLabel,
      poolStatus: p.poolStatus,
    }),
  );

  const view = buildFanProfileView({
    accountId: props.accountId,
    isProfileLoading: profileQuery.isPending && hasId,
    profile: profileQuery.data?.data?.profile,
    hasProfileFailed: profileQuery.isError || Boolean(profileQuery.data?.error),
    awards: awardsQuery.data?.data,
    areAwardsLoading: awardsQuery.isPending,
    hasAwardsFailed: awardsQuery.isError || Boolean(awardsQuery.data?.error),
    pools,
    arePoolsLoading: poolsQuery.isPending,
    hasPoolsFailed: poolsQuery.isError || Boolean(poolsQuery.data?.error),
  });

  // ISSUE-1370. The follow control is the server's `followState` rendered.
  // It is left off your own profile, where there is nobody to follow.
  const followState = profileQuery.data?.data?.followState;
  const followsYou = profileQuery.data?.data?.followsYou === true;
  // Your own profile has nobody to message, report or block, and every pool
  // in "Pools you share" is simply yours (QA round 1), so all of those are
  // left off too.
  const otherButtons = isSelf ? null : (
    <>
      <FanMessageAction accountId={props.accountId ?? ""} />
      <Button
        variant="ghost"
        onClick={() => {
          props.onOpenActions();
        }}
        data-testid="fan-more-actions"
      >
        {MORE_ACTIONS_LABEL}
      </Button>
    </>
  );

  const actions =
    !isSelf && isFollowState(followState) ? (
      <FanFollowAction
        accountId={props.accountId ?? ""}
        displayName={view.displayName}
        followState={followState}
        followsYou={followsYou}
        trailing={otherButtons}
      />
    ) : otherButtons === null ? null : (
      <div className="flex flex-wrap items-start justify-center gap-2 sm:justify-start">
        {otherButtons}
      </div>
    );

  return (
    <div className="flex flex-col gap-6 pb-24" data-testid="fan-profile">
      {view.phase === "noAccountId" ? (
        <p
          className="text-body text-ink-secondary"
          data-testid="fan-missing-id"
        >
          {PROFILE_MISSING_ID_LINE}
        </p>
      ) : null}

      {view.phase === "loading" ? (
        <div className="flex flex-col gap-2" data-testid="fan-loading">
          <Skeleton shape="text" />
          <Skeleton shape="text" />
          <Skeleton shape="text" />
        </div>
      ) : null}

      {view.phase === "unavailable" ? (
        <p
          className="text-body text-ink-secondary"
          data-testid="fan-unavailable"
        >
          {PROFILE_UNAVAILABLE_LINE}
        </p>
      ) : null}

      {view.phase === "private" ? (
        <>
          <FanProfileHeader
            displayName={view.displayName}
            username={view.username}
            photoUrl={view.photoUrl}
            actions={actions}
            followsYou={!isSelf && followsYou}
          />
          <p className="text-body text-ink-secondary" data-testid="fan-private">
            {PROFILE_PRIVATE_LINE}
          </p>
          {isSelf ? null : (
            <SharedPoolsList
              isLoading={view.arePoolsLoading}
              hasFailed={view.hasPoolsFailed}
              rows={view.pools}
              testId="fan-pools-section"
            />
          )}
        </>
      ) : null}

      {view.phase === "full" ? (
        <>
          <FanProfileHeader
            displayName={view.displayName}
            username={view.username}
            photoUrl={view.photoUrl}
            actions={actions}
            followsYou={!isSelf && followsYou}
          />
          <FanFavoriteTeamsRow
            teamIds={view.favoriteTeamIds}
            testId="fan-teams-section"
          />
          <MedalsBlock
            isLoading={view.areAwardsLoading}
            hasFailed={view.hasAwardsFailed}
            thisSeason={view.thisSeason}
            allTime={view.allTime}
            chips={view.chips}
            title={MEDALS_SECTION_TITLE}
            thisSeasonLabel={SCOPE_THIS_SEASON_LABEL}
            allTimeLabel={SCOPE_ALL_TIME_LABEL}
            tabsLabel={SCOPE_TABS_LABEL}
            unavailableMessage={AWARDS_UNAVAILABLE}
            testId="fan-medals"
          />
          {isSelf ? null : (
            <SharedPoolsList
              isLoading={view.arePoolsLoading}
              hasFailed={view.hasPoolsFailed}
              rows={view.pools}
              testId="fan-pools-section"
            />
          )}
        </>
      ) : null}
    </div>
  );
};
